import {
  Children,
  cloneElement,
  LegacyRef,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import type { Placement } from '@popperjs/core';
import { motion } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';
import create from 'zustand';

import { mergeRefs, useAriaId, usePreviousValue } from '@components/component-helpers';
import { theme } from '@styles/theme';
import { debounce } from '@utils/browser';
import { pxToRem } from '@utils/converters';

export interface TooltipProps {
  header?: string;
  content?: ReactElement | string;
  children: ReactElement;
  placement?: Placement;
}

type TriggerRef = MutableRefObject<HTMLElement | null> | LegacyRef<HTMLElement>;

export const Tooltip = ({ children, header, content, placement = 'bottom' }: TooltipProps) => {
  const hasContent = Boolean(header || content);
  const {
    //
    getArrowProps,
    getTooltipProps,
    getTriggerProps,
    setTooltipRef,
    visible,
  } = usePopperTooltip({
    placement,
    isEnabled: hasContent,
  });
  const fading = useGlobalTooltipState((state) => hasContent && state.fading);
  const wasFading = usePreviousValue(fading);

  const trigger = Children.only(children);

  if (!trigger) return null;

  // Merge with whatever ref the parent attached so wrappers like Menu keep
  // their floating-ui anchor when the trigger is also tooltipped.
  const triggerProps = getTriggerProps();
  const originalRef = (trigger as ReactElement & { ref?: TriggerRef }).ref;
  const refs: TriggerRef[] = [triggerProps.ref];
  if (originalRef) refs.push(originalRef);
  const mergedRef = mergeRefs<HTMLElement>(refs);

  return (
    <>
      {hasContent
        ? cloneElement(trigger, { ...trigger.props, ...triggerProps, ref: mergedRef })
        : trigger}

      {visible &&
        createPortal(
          <div ref={setTooltipRef} {...getTooltipProps()}>
            <motion.div
              className="tooltip-box"
              initial={{ opacity: wasFading ? 1 : 0 }}
              animate={{ opacity: 1, transition: { duration: 0.2 } }}
            >
              {header && <TooltipHeader>{header}</TooltipHeader>}
              {content && <TooltipContent>{content}</TooltipContent>}
              <motion.div {...getArrowProps()} />
            </motion.div>
          </div>,
          document.body,
        )}
    </>
  );
};

const TooltipHeader = styled.div({
  font: theme.font.bold14,
  whiteSpace: 'nowrap',
  height: '1.25rem',
});

const TooltipContent = styled.div({
  font: theme.font.semibold14,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
});

function usePopperTooltip({ placement, isEnabled }: { placement: Placement; isEnabled: boolean }) {
  const [triggerRef, setTriggerRef] = useState<HTMLElement | null>(null);
  const [tooltipRef, setTooltipRef] = useState<HTMLElement | null>(null);
  const id = useAriaId('tooltip');

  const hide = useGlobalTooltipState((state) => state.hide);
  const setShowTooltip = useGlobalTooltipState((state) => state.showTooltip);
  const isShowingSelf = useGlobalTooltipState((state) => state.showingId === id);

  const visible = isEnabled && isShowingSelf;

  const timer = useRef<number>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const { styles, attributes } = usePopper(triggerRef, tooltipRef, {
    modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
    placement,
  });

  const hideTooltip = useCallback(() => {
    clearTimeout(timer.current);
    hide();
  }, [hide]);

  useEffect(() => {
    if (isEnabled) return;
    clearTimeout(timer.current);
    if (isShowingSelf) hide();
  }, [isEnabled, isShowingSelf, hide]);

  const showTooltip = useCallback(() => {
    clearTimeout(timer.current);
    if (useGlobalTooltipState.getState().fading) {
      setShowTooltip(id);
    } else {
      timer.current = window.setTimeout(() => {
        setShowTooltip(id);
      }, 600);
    }
  }, [id, setShowTooltip]);

  const showTooltipOnFocus = useCallback(
    (event: FocusEvent) => {
      // Closing a modal puts focus back on whatever opened it, which would show
      // the tooltip again with the pointer somewhere else, and nothing left to
      // dismiss it. :focus-visible is false for that, true for keyboard focus.
      if (event.target instanceof Element && !event.target.matches(':focus-visible')) return;
      showTooltip();
    },
    [showTooltip],
  );

  useEffect(() => {
    if (triggerRef == null) return;

    triggerRef.addEventListener('focusin', showTooltipOnFocus);
    triggerRef.addEventListener('pointerenter', showTooltip);

    triggerRef.addEventListener('focusout', hideTooltip);
    triggerRef.addEventListener('pointerleave', hideTooltip);

    return () => {
      triggerRef.removeEventListener('focusin', showTooltipOnFocus);
      triggerRef.removeEventListener('pointerenter', showTooltip);

      triggerRef.removeEventListener('focusout', hideTooltip);
      triggerRef.removeEventListener('pointerleave', hideTooltip);
    };
  }, [triggerRef, showTooltip, showTooltipOnFocus, hideTooltip]);

  const getTooltipProps = () => ({
    role: 'tooltip',
    id: id,
    className: 'tooltip-container',
    style: styles.popper,
    ...attributes.popper,
  });

  const getArrowProps = () => ({
    ...attributes.arrow,
    className: 'tooltip-arrow',
    style: styles.arrow,
    'data-popper-arrow': true,
  });

  const getTriggerProps = () => ({
    'aria-describedby': id,
    ref: setTriggerRef,
  });

  return {
    getArrowProps,
    getTooltipProps,
    getTriggerProps,
    setTooltipRef,
    visible,
  };
}

interface GlobalTooltipState {
  showingId: string | null;
  fading: boolean;
  fade(): void;
  showTooltip(id: string): void;
  hide(): void;
}

const useGlobalTooltipState = create<GlobalTooltipState>((set, get) => ({
  showingId: null,
  fading: false,
  showTooltip: (showingId) => set({ showingId, fading: true }),
  fade: debounce(() => {
    set({ fading: false });
  }, 1000),
  hide: () => {
    set({ showingId: null, fading: true });
    get().fade();
  },
}));

export const TooltipStyles = createGlobalStyle`
  .tooltip-container {
    --tooltip-bg: ${theme.color.indigo100};
    --tooltip-border: ${theme.color.indigo100};
    --tooltip-color: ${theme.color.white100};
    position: relative;
    z-index: 999999;
  }

  .tooltip-box {
    position: relative;
    background-color: var(--tooltip-bg);
    border-radius: ${pxToRem(4)};
    border: ${pxToRem(1)} solid var(--tooltip-border);
    box-shadow: ${theme.shadow.medium};
    font: ${theme.font.regular14};
    color: var(--tooltip-color);
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: ${pxToRem(16)};
    max-width: 20rem;
    flex-shrink: 1;
  }

  .tooltip-arrow {
    height: 1rem;
    position: absolute;
    width: 1rem;
    pointer-events: none;
  }

  .tooltip-arrow::before {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    width: 0;
  }

  .tooltip-arrow::after {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    position: absolute;
    width: 0;
  }

  .tooltip-container[data-popper-placement*='bottom'] .tooltip-arrow {
    margin-top: -0.5rem;
    top: 0;
  }

  .tooltip-container[data-popper-placement*='bottom'] .tooltip-arrow::before {
    border-color: transparent transparent var(--tooltip-border) transparent;
    border-width: 0rem 0.5rem 0.5rem 0.5rem;
    position: absolute;
  }

  .tooltip-container[data-popper-placement*='bottom'] .tooltip-arrow::after {
    border-color: transparent transparent var(--tooltip-bg) transparent;
    border-width: 0 0.5rem 0.5rem 0.5rem;
  }

  .tooltip-container[data-popper-placement*='top'] .tooltip-arrow {
    bottom: 0;
    left: 0;
    margin-bottom: -1rem;
  }

  .tooltip-container[data-popper-placement*='top'] .tooltip-arrow::before {
    border-color: var(--tooltip-border) transparent transparent transparent;
    border-width: 0.5rem 0.5rem 0 0.5rem;
    position: absolute;
  }

  .tooltip-container[data-popper-placement*='top'] .tooltip-arrow::after {
    border-color: var(--tooltip-bg) transparent transparent transparent;
    border-width: 0.5rem 0.5rem 0 0.5rem;
  }

  .tooltip-container[data-popper-placement*='right'] .tooltip-arrow {
    left: 0;
  }

  .tooltip-container[data-popper-placement*='right'] .tooltip-arrow::before {
    border-color: transparent var(--tooltip-border) transparent transparent;
    border-width: 0.5rem 0.5rem 0.5rem 0;
  }

  .tooltip-container[data-popper-placement*='right'] .tooltip-arrow::after {
    border-color: transparent var(--tooltip-bg) transparent transparent;
    border-width: 0.5rem 0.5rem 0.5rem 0;
    left: -0.5rem;
    top: 0;
  }

  .tooltip-container[data-popper-placement*='left'] .tooltip-arrow {
    right: 0;
  }

  .tooltip-container[data-popper-placement*='left'] .tooltip-arrow::before {
    border-color: transparent transparent transparent var(--tooltip-border);
    border-width: 0.5rem 0 0.5rem 0.5rem;
  }

  .tooltip-container[data-popper-placement*='left'] .tooltip-arrow::after {
    border-color: transparent transparent transparent var(--tooltip-bg);
    border-width: 0.5rem 0 0.5rem 0.5rem;
    left: 1rem;
    top: 0;
  }
`;
